//React
import { useState, useEffect, useMemo, useContext } from 'react';
// Microsoft
import { app, monetization } from '@microsoft/teams-js';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
// Utils
import {
  callFunction,
  loadAppsData,
  loadReadinessData,
} from '../../lib/Helpers';
import { isMobile } from 'react-device-detect';
import * as rdd from 'react-device-detect';
// Components
import { AppTabsMenu } from '../../widgets/AppTabsMenu';
import { AppCardsGrid } from '../../widgets/AppCardsGrid';
import { BannerCarousel } from '../../widgets/BannerCarousel';
import { ReadinessObject } from '../../widgets/ReadinessCard';
import { AppObject } from '../../widgets/AppCard';
import SSO from '../../widgets/SSO';
// Styling
import { useBoolean } from '@fluentui/react-hooks';
import {
  BannerMobile,
  CardsGridContainer,
  MyHubContainer,
  SectionContainer,
} from './styles';
import { TrialAlert } from '../../widgets/TrialAlert';
import { ReadinessCardsGrid } from '../../widgets/ReadinessCardsGrid';
import { XCircle } from '@phosphor-icons/react';
import { Alert } from '@fluentui/react-components/unstable';
import { TeamsFxContext } from '../../Context';
import NoApps from '../../widgets/NoApps';
import { useTranslation } from 'react-i18next';
import { TutorialsContext } from '../../contexts/TutorialsContext';
import { HelpPanel } from '../../widgets/HelpPanel';
import {
  FluentProvider,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import { TutorialPopover } from '../../widgets/TutorialPopover';
import { LicencePopup } from '../../widgets/LicencePopup';

export type EntObject = {
  apps: string[];
  subscriptions: any[];
  hasTried: boolean;
  tutorials: string[];
};

interface PageProps {
  context: app.Context | undefined;
}

export const MyHub = ({ context }: PageProps) => {
  const userId = context?.user?.id ?? '';
  const tenantId = context?.user?.tenant?.id ?? '';
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const appInsights = useAppInsightsContext();

  const [isSSO, setIsSSO] = useState<boolean>(false);
  const [exception, setException] = useState<string>('');

  const [userEntitlements, setUserEntitlements] = useState<EntObject>();
  const [allApps, setAllApps] = useState<AppObject[] | undefined>();
  const [userApps, setUserApps] = useState<AppObject[] | undefined>();

  const [readinessData, setReadiness] = useState<ReadinessObject[] | undefined>(
    []
  );

  const [isTrial, { setTrue: inTrial, setFalse: notInTrial }] =
    useBoolean(false);

  const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
  const [hasClosedPopover, setHasClosedPopover] = useState<boolean>(false);
  const [firstRunEnabled, setFirstRunEnabled] = useState<boolean>(false);

  const [isLicencePopupVisible, setIsLicencePopupVisible] =
    useState<boolean>(false);
  const [licencePopupType, setLicencePopupType] = useState<
    'TRIAL' | 'HAS_TRIED'
  >();
  const [hasShownLicencePopup, setHasShownLicencePopup] =
    useState<boolean>(false);

  useEffect(() => {
    app.notifySuccess();
  }, []);

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      if (userId && tenantId) {
        loadAppsData(
          userId,
          setAllApps,
          setUserEntitlements,
          tenantId as string,
          teamsUserCredential
        ).catch((error) => {
          if (process.env.REACT_APP_APP_INSIGHTS !== '')
            appInsights.trackException({
              exception: error,
              properties: {
                component: 'MyHub',
                location: 'onLoadAppsData',
              },
            });
        });

        loadReadinessData(userId, setReadiness).catch((error) => {
          if (process.env.REACT_APP_APP_INSIGHTS !== '') {
            appInsights.trackException({
              exception: error,
              properties: {
                component: 'MyHub',
                location: 'onLoadReadinessData',
              },
            });
          }
        });
      }
    }

    return () => {
      mounted = false;
    };
  }, [appInsights, teamsUserCredential, tenantId, userId]);

  useEffect(() => {
    if (userEntitlements) {
      if (!hasShownLicencePopup && userEntitlements.subscriptions.length <= 0) {
        userEntitlements.hasTried
          ? setLicencePopupType('HAS_TRIED')
          : setLicencePopupType('TRIAL');
        setIsLicencePopupVisible(true);
        setHasShownLicencePopup(true);
      }

      userEntitlements.subscriptions.forEach((sub) => {
        if (sub.isFreeTrial && userEntitlements.subscriptions.every(() => sub))
          inTrial();
      });

      setUserApps(
        allApps?.filter((app) => userEntitlements.apps.includes(app.id))
      );

      if (!userEntitlements.tutorials.includes('myhub')) {
        !hasClosedPopover && setIsPopoverVisible(true);
        setFirstRunEnabled(true);
      }
    }
  }, [allApps, hasClosedPopover, inTrial, userEntitlements]);

  const steps = useMemo(() => ['allapps', 'myapps'], []);

  const { t } = useTranslation(['myhub', 'alerts']);

  const friendlyStepsName: { [key: string]: string } = {
    allapps: t('ALL_BITFRACTAL_APPS'),
    myapps: t('MY_AVAILABLE_APPS'),
  };

  const friendlyStepsNameMobile: { [key: string]: string } = {
    allapps: t('ALL_BITFRACTAL_APPS_MOBILE'),
    myapps: t('MY_AVAILABLE_APPS_MOBILE'),
  };

  const [selectedMenuItem, setSelectedMenuItem] = useState('allapps');

  const items = steps.map((step) => {
    return {
      label: !isMobile
        ? friendlyStepsName[step] || ''
        : friendlyStepsNameMobile[step] || '',
      action: () => setSelectedMenuItem(step),
      isSelected: step === selectedMenuItem,
    };
  });

  const openPurchaseExperience = () => {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({ name: 'Upgrade from MyHub clicked' });
      if (monetization.isSupported()) {
        monetization
          .openPurchaseExperience()
          .catch(() =>
            window.open(encodeURI('https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'))
          );
      } else {
        window.open(encodeURI('https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'));
      }
    }
  };

  const initiateTrialExperience = () => {
    callFunction(
      true,
      'putTrialSubForUser?{0}&tenantId=' + tenantId,
      undefined,
      false,
      teamsUserCredential
    )
      .then(() => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackEvent({
            name: 'Trial initiated from MyHub clicked',
          });
        window.location.reload();
      })
      .catch(() => {
        alert(
          'Unable to initiate trial, please try again later or contact support if the problem persists.'
        );
      });
  };

  return (
    <>
      {isTrial && <TrialAlert />}
      {!context && (
        <Alert intent="warning" appearance="inverted">
          {t('NOT_IN_TEAMS', { ns: 'alerts' })}
        </Alert>
      )}
      {exception !== '' && (
        <Alert
          intent="error"
          action={{
            icon: <XCircle color="#fd6262" />,
            onClick: () => setException(''),
          }}
        >
          {t('APP_ERROR', { ns: 'alerts' })}
        </Alert>
      )}

      {!isMobile ? (
        <MyHubContainer>
          <BannerCarousel />
          <SectionContainer>
            <AppTabsMenu items={items} />
            {selectedMenuItem === 'allapps' && (
              <>
                <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
                {isSSO && userEntitlements?.apps?.length === 0 && (
                  <NoApps
                    onUpgrade={openPurchaseExperience}
                    hasTried={userEntitlements?.hasTried}
                    compact
                    tid={tenantId}
                  />
                )}
                <CardsGridContainer>
                  <AppCardsGrid
                    tenantId={tenantId}
                    sso={isSSO}
                    apps={allApps}
                    entitlements={userEntitlements}
                  />
                </CardsGridContainer>
              </>
            )}
            {selectedMenuItem === 'myapps' && (
              <>
                <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
                {isSSO && userEntitlements?.apps?.length === 0 && (
                  <NoApps
                    onUpgrade={openPurchaseExperience}
                    hasTried={userEntitlements?.hasTried}
                    compact
                    tid={tenantId}
                  />
                )}
                <CardsGridContainer>
                  <AppCardsGrid
                    tenantId={tenantId}
                    sso={isSSO}
                    apps={userApps}
                    entitlements={userEntitlements}
                  />
                </CardsGridContainer>
              </>
            )}
          </SectionContainer>
          <SectionContainer>
            <h1>{t('READINESS_TITLE')}</h1>
            <CardsGridContainer id="step-learning-and-resources" isReadiness>
              <ReadinessCardsGrid sso={isSSO} readiness={readinessData} />
            </CardsGridContainer>
          </SectionContainer>
          <HelpPanel appId="myhub" firstRunEnabled={firstRunEnabled} />

          <TutorialPopover
            appId="myhub"
            scope="myhub"
            dimissPopover={() => {
              setIsPopoverVisible(false);
              setHasClosedPopover(true);
            }}
            title="MyHub"
            isVisible={isPopoverVisible}
          />
          {licencePopupType && (
            <LicencePopup
              isVisible={isLicencePopupVisible}
              setIsVisible={setIsLicencePopupVisible}
              tid={tenantId}
              type={licencePopupType}
            />
          )}
        </MyHubContainer>
      ) : (
        <MyHubContainer>
          <BannerMobile>
            <img src="cube.png" alt="" />
            <div className="text">
              <h1>BitFractal</h1>
              <h2>AI for Business Made Simple</h2>
            </div>
          </BannerMobile>
          <SectionContainer>
            <AppTabsMenu items={items} />
            {selectedMenuItem === 'allapps' && (
              <>
                <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
                <CardsGridContainer>
                  <AppCardsGrid
                    tenantId={tenantId}
                    sso={isSSO}
                    apps={allApps}
                    entitlements={userEntitlements}
                  />
                </CardsGridContainer>
              </>
            )}
            {selectedMenuItem === 'myapps' && (
              <>
                <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
                {isSSO && userEntitlements?.apps?.length === 0 && (
                  <NoApps
                    onUpgrade={openPurchaseExperience}
                    hasTried={userEntitlements?.hasTried}
                    compact
                    tid={tenantId}
                  />
                )}
                <CardsGridContainer>
                  <AppCardsGrid
                    tenantId={tenantId}
                    sso={isSSO}
                    apps={userApps}
                    entitlements={userEntitlements}
                  />
                </CardsGridContainer>
              </>
            )}
          </SectionContainer>
          <SectionContainer>
            <h1>{t('READINESS_TITLE')}</h1>
            <CardsGridContainer isReadiness>
              <ReadinessCardsGrid
                sso={isSSO}
                readiness={readinessData}
                isMobile
              />
            </CardsGridContainer>
          </SectionContainer>
        </MyHubContainer>
      )}
    </>
  );
};
